import React from "react";
import styled from "styled-components/macro";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Card from "../../components/Card";
import Action from "../../components/Action";
import QuoteGuarantees from "../QuoteCommon/QuoteGuarantees";
import List, { ListItem } from "../../components/List";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import TrustpilotBanner from "../../components/TrustpilotBanner";
import { formatMoneyWithCurrency, moneyStrToNum } from "../../shared/utils/moneyUtils";
import { fireSignupStartedConversion } from "../../utils/adsConversionUtils";
import { ListItemSuffix, QuoteBreakdown, QuotedPrice, QuotedPriceWrapper } from "../QuoteCommon/QuoteStyles";

// The navy button is only 2.8:1 against the green price panel, under the 3:1
// guideline, so the one in the panel is white. The panel's own link style
// would turn its text white too.
const LightButton = styled.div`
  --juno-color-button: var(--juno-color-white);
  --juno-color-button-text: var(--juno-color-dark-blue);
  --juno-color-button-hover: var(--juno-color-off-white);

  && a:link,
  && a:visited {
    color: var(--juno-color-button-text);
    border-bottom: 2px solid var(--juno-color-button-shadow);
  }
`;

function QuoteDetailsPage(props) {
  const { pageWidth, referral, quoteDetails, buyingDetails, sellingDetails, transactionType } = props;

  let saleLegalFee = "";
  let salePropertyPrice = "";
  let purchaseLegalFee = "";
  let purchasePropertyPrice = "";
  let quotePrice = "";
  let stampDuty = "";
  let stampDutyDescription = "Stamp Duty (paid to the government)";
  let landRegistryFee = "";
  let companiesHouseRegistrationFee = "";
  let totalFeesAndTaxes = "";
  let sellingTenure = "";
  let buyingTenure = "";
  const formatQuotedAmount = (value, showDecimal) => {
    if (!value) return "";
    return formatMoneyWithCurrency(moneyStrToNum(value).toString(), showDecimal);
  };

  let includeDecimal = landRegistryFee.includes(".") || stampDuty.includes(".");

  if (quoteDetails) {
    quotePrice = formatQuotedAmount(quoteDetails.totalPrice, includeDecimal);
    saleLegalFee = formatQuotedAmount(quoteDetails.sellingQuotePrice, includeDecimal);
    purchaseLegalFee = formatQuotedAmount(quoteDetails.buyingQuotePrice, includeDecimal);
    stampDuty = formatQuotedAmount(quoteDetails.stampDutyFee, includeDecimal);
    landRegistryFee = formatQuotedAmount(quoteDetails.landRegistryFee, includeDecimal);
    companiesHouseRegistrationFee = formatQuotedAmount(quoteDetails.companiesHouseRegistrationFee, includeDecimal);
    totalFeesAndTaxes = formatQuotedAmount(quoteDetails.totalFeesAndTaxes, includeDecimal);

    if (buyingDetails && !!buyingDetails.firstTimeBuyer && !!buyingDetails.buyToLet) {
      stampDutyDescription = "Stamp Duty (paid to the government, no first-time buyer relief)";
    }

    purchasePropertyPrice =
      transactionType === "Buying" || transactionType === "BuyingAndSelling"
        ? formatQuotedAmount(buyingDetails?.price, includeDecimal)
        : "";

    salePropertyPrice =
      transactionType === "Selling" || transactionType === "BuyingAndSelling"
        ? formatQuotedAmount(sellingDetails?.price, includeDecimal)
        : "";
  }

  function getTenure(details) {
    if (details?.tenure === "leasehold with share of freehold") {
      return "share of freehold";
    }
    return details.tenure;
  }

  if (transactionType === "Selling" || transactionType === "BuyingAndSelling") {
    sellingTenure = getTenure(sellingDetails);
  }

  if (transactionType === "Buying" || transactionType === "BuyingAndSelling") {
    buyingTenure = getTenure(buyingDetails);
  }

  const transactionTypes = {
    Buying: {
      signupTransaction: "purchase",
      quotePriceText: `That covers all our legal work on your ${buyingTenure} purchase at ${purchasePropertyPrice}, including VAT.`,
    },
    Selling: {
      signupTransaction: "sale",
      quotePriceText: `That covers all our legal work on your ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`,
    },
    BuyingAndSelling: {
      signupTransaction: "sale and purchase",
      quotePriceText: `That covers all our legal work on your ${buyingTenure} purchase at ${purchasePropertyPrice} and ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`,
    },
  };

  const { signupTransaction, quotePriceText } = transactionTypes[transactionType] || {};

  // A full page load, not a router link, so the quote flow's Ads tag doesn't
  // run on the sign-up (see appConfig.gohtml).
  const signupButton = (
    <Action as="a" href="/continue-signup" onClick={() => fireSignupStartedConversion(quoteDetails)}>
      Sign up now
    </Action>
  );
  const continuationOffered = quoteDetails?.continuationOffered;

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="Here’s your quote" removeTopPadding>
        <QuotedPriceWrapper>
          <QuotedPrice>{quotePrice}</QuotedPrice>
          <p>{quotePriceText}</p>

          {continuationOffered && (
            <>
              <LightButton>{signupButton}</LightButton>
              <p>Your price is held for 30 days. No obligation, and nothing to pay to sign up.</p>
            </>
          )}

          <QuoteGuarantees transactionType={transactionType} />
        </QuotedPriceWrapper>

        {referral && <ReferralCodeBanner text={referral.quoteMessage} withinQuote={true} />}

        {(transactionType === "Buying" || transactionType === "BuyingAndSelling") && (
          <QuoteBreakdown>
            <List>
              {transactionType === "BuyingAndSelling" ? (
                <>
                  <ListItem divider>
                    Our legal fee for your {sellingTenure} sale
                    <ListItemSuffix>{saleLegalFee}</ListItemSuffix>
                  </ListItem>
                  <ListItem divider>
                    Our legal fee for your {buyingTenure} purchase
                    <ListItemSuffix>{purchaseLegalFee}</ListItemSuffix>
                  </ListItem>
                </>
              ) : (
                <ListItem divider>
                  Our legal fee for your {buyingTenure} purchase
                  <ListItemSuffix data-testid="up-front-payment">{purchaseLegalFee}</ListItemSuffix>
                </ListItem>
              )}

              <ListItem divider>
                Land Registry fee (paid to the government)
                <ListItemSuffix data-testid="land-registry-fee">{landRegistryFee}</ListItemSuffix>
              </ListItem>
              <ListItem divider>
                {stampDutyDescription}
                <ListItemSuffix data-testid="stamp-duty-fee">{stampDuty}</ListItemSuffix>
              </ListItem>
              {buyingDetails.clientIsCompany && (
                <ListItem divider>
                  Companies house registration fee
                  <ListItemSuffix data-testid="companies-house-registration-fee">
                    {companiesHouseRegistrationFee}
                  </ListItemSuffix>
                </ListItem>
              )}

              <ListItem divider>
                Total, including government fees and taxes
                <ListItemSuffix data-testid="total-fees-and-taxes">{totalFeesAndTaxes}</ListItemSuffix>
              </ListItem>
            </List>
          </QuoteBreakdown>
        )}
      </Card>

      {continuationOffered ? (
        <Card title="When you sign up" removeTopPadding headingSize="3">
          <List number>
            <ListItem>
              Tell us about your {signupTransaction}. It’s all online, and we’ve filled in your name and contact
              details.
            </ListItem>
            <ListItem>We’ll set up your case and email you a short form to fill in.</ListItem>
            <ListItem>
              Then complete your ID checks online on your Juno dashboard. There’s no need to visit an office.
            </ListItem>
          </List>

          {signupButton}

          <p>We’ll also email you a copy of this quote. Questions? We’ll call you to answer them.</p>
        </Card>
      ) : (
        <Card title="What happens next" removeTopPadding headingSize="3">
          <List number>
            <ListItem>Now check your inbox for the full quote.</ListItem>
            <ListItem>When you’re ready, sign up from the email.</ListItem>
            <ListItem>We’ll call you to answer any questions.</ListItem>
          </List>

          <p>There’s no obligation, and your price is held for 30 days.</p>
        </Card>
      )}

      <TrustpilotBanner pageWidth={pageWidth} />
    </React.Fragment>
  );
}

export default QuoteDetailsPage;
